// src/components/solutions/SolutionDetails.jsx
import React, { useState, useContext } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Button,
  Box,
  Typography,
  Chip,
  Stack,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Divider,
  Avatar,
} from "@mui/material";
// import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { motion } from "framer-motion";
import {
  FaInfoCircle,
  FaLightbulb,
  FaCheckCircle,
  FaTools,
  FaUserShield,
  FaUserCog,
} from "react-icons/fa";
import AddProposal from "./forms/AddProposal";
import ReviseProposal from "./forms/ReviseProposal";
import Implemented from "./forms/Implemented";
import { AuthContext } from "../../auth/AuthContext";
import MediaPreview from "../../utils/MediaPreview";
import { useNavigate } from "react-router-dom";


import ReadOnlyEditor from "../../components/tiptap-templates/simple/read-only-editor";

const formatDate = (d) => (d ? new Date(d).toLocaleString() : "N/A");

const StepCard = ({ icon, title, content, iconColor = "#2e7d32" }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.5 }}
    style={{ marginBottom: "2rem" }}
  >
    <Paper
      elevation={4}
      sx={{
        p: 3,
        borderRadius: 4,
        background: "linear-gradient(135deg, #f1f8e9, #e1f5fe)",
      }}
    >
      <Stack direction="row" spacing={2} alignItems="flex-start">
        <Avatar sx={{ bgcolor: iconColor }}>{icon}</Avatar>
        <Box>
          <Typography variant="h6" fontWeight="bold" color="primary.dark">
            {title}
          </Typography>
          <Divider sx={{ my: 1 }} />
          <Box>{content}</Box>
        </Box>
      </Stack>
    </Paper>
  </motion.div>
);

const SolutionDetails = ({ solution, reload, setReload , issue }) => {
  const [open, setOpen] = useState(false);
  const { currentUser, authToken } = useContext(AuthContext);
  const navigate = useNavigate();

  const hasAlreadyReviewed = !!issue && issue?.reviews.some(
    (review) => review.user?.user_id === currentUser?.id
  );
  const canAddReview = !hasAlreadyReviewed && !!currentUser && !!authToken;
  // console.log(issue)


  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  if (!solution?.assigned_to)
    return (
      <Typography color="text.secondary" align="center">
        No Authority/specialist has been tasked yet
      </Typography>
    );

  // Define steps
  const steps = ["Details", "Proposal", "Revision", "Approval", "Implementation"];

  const getStepIndex = (status) => {
    switch (status) {
      case "Pending":
        return 0;
      case "Proposed":
      case "Needs Revision":
        return 1;
      case "Revision Requested":
        return 2;
      case "Approved":
        return 3;
      case "Implemented":
        return 4;
      default:
        return 0;
    }
  };

  const activeStep = getStepIndex(solution.status) + 1 ;

  // async function handleAddProposal(){

  // }
  // console.log(solution)

  return (
    <>
      {/* Trigger Button */}
      <Button
        variant="outlined"
        color="primary"
        // size="small"
        startIcon={<VisibilityIcon />}
        onClick={handleOpen}
      >
        View Solution Progress {' '}
        <Chip
            label={solution.status}
            onClick={handleOpen}
            color={
              solution.status === "Pending"
                ? "warning"
                : solution.status === "Implemented"
                ? "info"
                : solution.status === "Approved"
                ? "primary"
                : solution.status === "Needs Revision"
                ? "warning"
                : "success"
            }
            sx={{ fontWeight: "bold" }}
            size="small"
          />
      </Button>

      {/* Popup Dialog */}
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle sx={{ m: 0, p: 2 }}>
          <Typography
            variant="h4"
            fontWeight={700}
            gutterBottom
            color="success.main"
            display="flex"
            alignItems="center"
            gap={1}
          >
            <FaCheckCircle /> Solution Progress
            <Chip
              label={solution.status}
              color={
                solution.status === "Pending"
                  ? "warning"
                  : solution.status === "Implemented"
                  ? "info"
                  : solution.status === "Approved"
                  ? "primary"
                  : solution.status === "Needs Revision"
                  ? "warning"
                  : "success"
              }
              sx={{ fontWeight: "bold", ml: 2 }}
            />
          </Typography>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            ❌
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 3 }}>
          {/* Stepper */}
          <Box sx={{ mb: 4 }}>
            <Stepper activeStep={activeStep} alternativeLabel>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>

          {/* Steps Content */}
          <StepCard
            icon={<FaInfoCircle />}
            title="Details"
            content={
              <Box
                sx={{
                  maxWidth: "100%",
                  overflowX: "auto",
                  wordWrap: "break-word",
                  whiteSpace: "normal",
                  "& a": {
                    color: "primary.main",
                    textDecoration: "none",
                    "&:hover": {
                      textDecoration: "underline",
                    },
                  },
                }}
              >
                <Typography>
                  <strong>Status:</strong>{" "}
                  <Chip
                    label={solution.status}
                    color={
                      solution.status === "Pending"
                        ? "warning"
                        : solution.status === "Implemented"
                        ? "info"
                        : solution.status === "Approved"
                        ? "primary"
                        : solution.status === "Needs Revision"
                        ? "warning"
                        : "success"
                    }
                    sx={{ fontWeight: "bold" }}
                  />
                </Typography>

                <Typography noWrap={false}>
                  <strong>Assigned To:</strong> {solution.assigned_to?.name} (
                  {solution.assigned_to?.organisation})
                </Typography>

                <Typography sx={{ wordBreak: "break-all" }}>
                  <a href={`mailto:${solution.assigned_to?.email}`}>
                    {solution.assigned_to?.email}
                  </a>
                </Typography>

                <Typography sx={{ wordBreak: "break-all" }}>
                  <a href={`tel:${solution.assigned_to?.phone}`}>
                    {solution.assigned_to?.phone}
                  </a>
                </Typography>

                {solution.status === "Pending" &&
                  currentUser?.id === solution?.assigned_to?.user_id && (
                    <AddProposal
                      solutionId={solution?.solution_id}
                      reload={reload}
                      setReload={setReload}
                    />
                  )}
              </Box>

            }
          />

          {!!solution?.status &&
            ["Needs Revision", "Proposed", "Approved", "Implemented"].includes(
              solution.status
            ) && (
              <StepCard
                icon={<FaLightbulb />}
                title="Proposal"
                content={
                  <>
                    <Typography>
                      <strong>Proposed At:</strong>{" "}
                      {formatDate(solution.proposed_at)}
                    </Typography>
                    <Typography sx={{ whiteSpace: "pre-wrap", fontFamily:'Lato',fontStyle:'italic' }}>
                      <strong>Proposal Notes:</strong>
                      <br />
                      {/* {solution.proposal_notes || "N/A"} */}
                    </Typography>
                    <Box sx={{ width: "100%", height: "100%", bgcolor: "white", borderRadius: 2, p: 2, boxShadow: 1 }}>
                      <ReadOnlyEditor content={solution.proposal_notes || ""} />
                    </Box>
                    {solution.status === "Needs Revision" &&
                      currentUser?.id === solution?.assigned_to?.user_id && (
                        <ReviseProposal
                          solutionId={solution?.solution_id}
                          reload={reload}
                          setReload={setReload}
                          proposal_notes={solution?.proposal_notes}
                          revision_notes={solution?.revision_notes}
                        />
                      )}
                  </>
                }
              />
            )}

          {!!solution?.revision_notes && (
            <StepCard
              icon={<FaUserCog />}
              title="Revision Requested"
              content={
                <>
                  <Typography>
                    <strong>Revision Requested At:</strong>{" "}
                    {formatDate(solution.revision_requested_at)}
                  </Typography>
                  <Typography>
                    <strong>Requested By:</strong>{" "}
                    {solution.revision_requested_by?.name} (
                    {solution.revision_requested_by?.organisation})
                  </Typography>
                  <Typography>
                    <a href={`mailto:${solution.revision_requested_by?.email}`}>
                      {solution.revision_requested_by?.email}
                    </a>
                  </Typography>
                  <Typography sx={{ whiteSpace: "pre-wrap", fontFamily:'Lato',fontStyle:'italic'  }}>
                    <strong>Revision Notes:</strong>
                    {/* <br /> */}
                    
                  </Typography>

                    <Box sx={{ width: "100%", height: "100%", bgcolor: "white", borderRadius: 2, p: 2, boxShadow: 1 }}>
                      <ReadOnlyEditor content={solution.revision_notes || "N/A"} />
                    </Box>
                  {solution.status === "Needs Revision" &&
                    currentUser?.id === solution?.assigned_to?.user_id && (
                      <ReviseProposal
                        solutionId={solution?.solution_id}
                        reload={reload}
                        setReload={setReload}
                        proposal_notes={solution?.proposal_notes}
                        revision_notes={solution?.revision_notes}
                      />
                    )}
                </>
              }
            />
          )}

          {!!solution?.status &&
            ["Approved", "Implemented"].includes(solution.status) && (
              <StepCard
                icon={<FaUserShield />}
                title="Approval"
                content={
                  <>
                    <Typography>
                      <strong>Approved At:</strong>{" "}
                      {formatDate(solution.approved_at)}
                    </Typography>
                    <Typography>
                      <strong>Approved By:</strong>{" "}
                      {solution.approved_by?.name} (
                      {solution.approved_by?.organisation})
                    </Typography>
                    <Typography>
                      <a href={`mailto:${solution.approved_by?.email}`}>
                        {solution.approved_by?.email}
                      </a>
                    </Typography>
                    <Typography sx={{ whiteSpace: "pre-wrap", fontFamily:'Lato',fontStyle:'italic'  }}>
                      <strong>Approval Notes:</strong>
                      {/* <br /> */}
                      
                    </Typography>
                     <Box sx={{ width: "100%", height: "100%", bgcolor: "white", borderRadius: 2, p: 2, boxShadow: 1 }}>
                      <ReadOnlyEditor content={solution.approval_notes || "N/A"} />
                    </Box>
                    {solution.status === "Approved" &&
                      currentUser?.id === solution?.assigned_to?.user_id && (
                        <Implemented
                          solutionId={solution?.solution_id}
                          reload={reload}
                          setReload={setReload}
                        />
                      )}
                  </>
                }
              />
            )}

          {!!solution?.status && solution.status === "Implemented" && (
            <StepCard
              icon={<FaTools />}
              title="Implementation"
              content={
                <>
                  <Typography>
                    <strong>Implemented At:</strong>{" "}
                    {formatDate(solution.implemented_at)}
                  </Typography>
                  <Typography sx={{ whiteSpace: "pre-wrap", fontFamily:'Lato',fontStyle:'italic'  }}>
                    <strong>Implementation Notes:</strong>
                    {/* <br /> */}
                    
                  </Typography>

                     <Box sx={{ width: "100%", height: "100%", bgcolor: "white", borderRadius: 2, p: 2, boxShadow: 1 }}>
                      <ReadOnlyEditor content={solution.implementation_notes || "N/A"} />
                    </Box>
                  {solution.implementation_url && (
                    <MediaPreview
                      src={solution?.implementation_url}
                      alt={"solution Media"}
                    />
                  )}
                </>
              }
            />
          )}
        </DialogContent>
          {canAddReview && (
        <DialogActions alignItems="center" >
                    <Button
                      variant="contained"
                      color="success"
                      size="small"
                      sx={{
                        borderRadius: 3,
                        fontWeight: 'bold',
                        textTransform: 'none',
                        px: 3,
                        py: 1.2,
                        background: 'linear-gradient(45deg, #66bb6a, #26a69a)',
                        boxShadow: '0 4px 12px rgba(38, 166, 154, 0.25)',
                        '&:hover': {
                          background: 'linear-gradient(45deg, #43a047, #00897b)',
                        },
                      }}
                      onClick={() => navigate(`/reviews/add/${solution.issue_id}`)}
                    >
                      💬 Review
                    </Button>
        </DialogActions>
                  )}
      </Dialog>
    </>
  );
};

export default SolutionDetails;
